//
//  QaProgramChecklistActivityControl.tsx
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module opens the QA Program Checklist activity timeline.
//

import { ActionIcon, Badge, Group, Text, Tooltip } from "@mantine/core";
import type { FC } from "react";

import type {
    TrialQaProgramChecklistItemCompletionStateChange,
    TrialQaProgramChecklistItemNote
} from "src/api/Tmd.dtos";
import AppSettings from "src/AppSettings";
import Icon from "src/components/common/Icon";

import {
    buildQaProgramChecklistActivity,
    getQaProgramChecklistCompletionStateLabel
} from "./qaProgramChecklistActivity";

/** Supplies the task's activity and the handler that opens it. */
export interface QaProgramChecklistActivityControlProps {
    task: string;
    activityCount: number;
    notes: TrialQaProgramChecklistItemNote[];
    completionStateChanges: TrialQaProgramChecklistItemCompletionStateChange[];
    onOpenActivity: () => void;
}

/**
 * Opens a task's activity timeline and indicates how many entries it already holds.
 *
 * Postconditions: An empty timeline still renders a dimmed history icon, because this control is
 * where a first note is added and rendering nothing would be a dead end. A populated timeline
 * shows its count and previews its latest entry on hover.
 */
const QaProgramChecklistActivityControl: FC<QaProgramChecklistActivityControlProps> = ({
    task,
    activityCount,
    notes,
    completionStateChanges,
    onOpenActivity
}) => {
    const latest = buildQaProgramChecklistActivity(notes, completionStateChanges).at(-1);
    const preview = latest
        ? latest.kind === "note"
            ? latest.note.comments
            : `Completion changed to ${getQaProgramChecklistCompletionStateLabel(
                  latest.change.completionState
              )}`
        : "Add the first note";

    return (
        <Tooltip
            label={
                <Text
                    style={{ whiteSpace: "pre-wrap" }}
                    size="sm"
                >
                    {preview}
                </Text>
            }
            withArrow
            multiline
        >
            <Group
                gap={4}
                wrap="nowrap"
            >
                <ActionIcon
                    variant="subtle"
                    color={activityCount > 0 ? "primary" : "gray"}
                    aria-label={`Activity for ${task}`}
                    onClick={onOpenActivity}
                >
                    <Icon icon={AppSettings.icons.previousVersion} />
                </ActionIcon>
                <Badge
                    color="secondary.6"
                    autoContrast
                    size="sm"
                    circle
                    // Kept mounted so this control is the same width on every row, which is what
                    // keeps the completion control beside it aligned down the list. Hiding rather
                    // than dimming also keeps an empty count out of the accessibility tree.
                    style={{ visibility: activityCount > 0 ? "visible" : "hidden" }}
                >
                    {activityCount}
                </Badge>
            </Group>
        </Tooltip>
    );
};

export default QaProgramChecklistActivityControl;
